var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMutation, useQuery } from '@apollo/client';
import { DateTimePicker } from '@mui/lab';
import { Box, Button, Chip, DialogActions, FormControl, InputLabel, MenuItem, OutlinedInput, Select, TextField, useTheme, } from '@mui/material';
import { SnackbarSeverity } from 'common/enums/components/snackbar-severity.enum';
import { StandingsParentType } from 'common/enums/standings/standings-parent-type.enum';
import { StandingsParticipantType } from 'common/enums/standings/standings-participant-type.enum';
import { CREATE_STANDINGS } from 'common/graphql/mutations/standings/create-standings';
import { SPORTS } from 'common/graphql/queries/sports/sports';
import { TOURNAMENT } from 'common/graphql/queries/tournament/tournament';
import useDynamicMutation from 'common/hooks/useDynamicMutation';
import standingsValidationSchema from 'common/validationSchemas/standingsValidationSchema';
import CircularProgressMui from 'components/Loading/CircularProgresMui';
import { useFormik } from 'formik';
import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { openSnackbar } from 'store/actions';
var ITEM_HEIGHT = 48;
var ITEM_PADDING_TOP = 8;
var MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250
        }
    }
};
function getStyles(name, personName, theme) {
    return {
        fontWeight: personName.indexOf(name) === -1 ? theme.typography.fontWeightRegular : theme.typography.fontWeightMedium
    };
}
var CreateTournamentStandingsDialogDialog = function (_a) {
    var _b, _c;
    var tournamentId = _a.tournamentId, tournamentStartDate = _a.tournamentStartDate, tournamentEndDate = _a.tournamentEndDate, handleClose = _a.handleClose;
    var theme = useTheme();
    var defaultFormState = {
        name: '',
        displayName: '',
        activityStartDate: null,
        activityEndDate: null,
        participantType: StandingsParticipantType.User
    };
    var dispatch = useDispatch();
    var t = useTranslation().t;
    var _d = useState([]), sportNames = _d[0], setSportNames = _d[1];
    var _e = useState(StandingsParticipantType.User), participantType = _e[0], setParticipantType = _e[1];
    var participantTypes = Object.values(StandingsParticipantType);
    var _f = useQuery(SPORTS), sportsData = _f.data, loading = _f.loading;
    var sportObjects = (sportsData === null || sportsData === void 0 ? void 0 : sportsData.sports) || [];
    var sports = ((_c = (_b = sportsData === null || sportsData === void 0 ? void 0 : sportsData.sports) === null || _b === void 0 ? void 0 : _b.filter(function (sport) { return sport.id !== '64908c46-8569-4d00-af0f-225106161bb9'; })) === null || _c === void 0 ? void 0 : _c.map(function (sport) { return sport.displayName; })) || [];
    var createStandings = function (data) {
        return useMutation(CREATE_STANDINGS, {
            variables: {
                createStandingsData: __assign({}, data)
            },
            awaitRefetchQueries: true,
            refetchQueries: [
                {
                    query: TOURNAMENT,
                    variables: {
                        id: tournamentId,
                        showStandings: true
                    }
                },
            ]
        });
    };
    var handleChangeWrapper = function (e) {
        formik.setFieldError(e.target.name, undefined); // <- reset error on the field
        formik.handleChange(e); // <- call original Formik's change handler
    };
    var onSuccess = function () {
        dispatch(openSnackbar({
            message: 'Турнирная таблица создана.',
            severity: SnackbarSeverity.SUCCESS
        }));
        handleClose();
    };
    var onError = function (_a) {
        var code = _a.code, errorDetails = _a.errorDetails;
        dispatch(openSnackbar({
            message: errorDetails || code,
            severity: SnackbarSeverity.ERROR
        }));
    };
    var handleSportsChange = function (event) {
        var value = event.target.value;
        setSportNames(typeof value === 'string' ? value.split(',') : value);
    };
    var handleTournamentStandingsTypeChange = function (event) {
        var value = event.target.value;
        setParticipantType(value);
    };
    var handleExecuteMutation = useDynamicMutation(createStandings, onSuccess, onError).handleExecuteMutation;
    var handleSubmit = function (data) {
        var _a;
        var sportIds = [];
        for (var _i = 0, sportNames_1 = sportNames; _i < sportNames_1.length; _i++) {
            var s = sportNames_1[_i];
            var id = (_a = _.find(sportObjects, { displayName: s })) === null || _a === void 0 ? void 0 : _a.id;
            id && sportIds.push(id);
        }
        var createObj = {
            name: data.name,
            displayName: data.displayName,
            activitySettings: {
                activityStartDate: new Date(data.activityStartDate),
                activityEndDate: new Date(data.activityEndDate)
            },
            sportIds: sportIds,
            parentId: tournamentId,
            parentType: StandingsParentType.Tournament,
            participantType: participantType
        };
        handleExecuteMutation(__assign({}, createObj));
    };
    var formik = useFormik({
        initialValues: __assign({}, defaultFormState),
        validationSchema: standingsValidationSchema.pick(Object.keys(defaultFormState)),
        onReset: handleClose,
        onSubmit: handleSubmit,
        validateOnChange: false,
        validateOnBlur: false
    });
    if (loading)
        return _jsx(CircularProgressMui, {}, void 0);
    return (_jsxs("form", __assign({ onSubmit: formik.handleSubmit, onReset: formik.handleReset }, { children: [_jsxs("div", __assign({ className: "flex flex-col p-0 pb-20" }, { children: [_jsx("div", __assign({ className: "tw-dialog-header" }, { children: "\u0421\u043E\u0437\u0434\u0430\u0442\u044C \u0442\u0443\u0440\u043D\u0438\u0440\u043D\u0443\u044E \u0442\u0430\u0431\u043B\u0438\u0446\u0443" }), void 0), _jsxs("div", __assign({ className: "flex flex-col space-y-20" }, { children: [_jsx(TextField, { className: "", label: "\u0423\u043D\u0438\u043A\u0430\u043B\u044C\u043D\u043E\u0435 \u0438\u043C\u044F", name: "name", value: formik.values['name'], onChange: handleChangeWrapper, error: Boolean(formik.errors['name']), helperText: formik.errors['name'] }, void 0), _jsx(TextField, { className: "", label: "\u041E\u0442\u043E\u0431\u0440\u0430\u0436\u0430\u0435\u043C\u043E\u0435 \u0438\u043C\u044F", name: "displayName", value: formik.values['displayName'], onChange: handleChangeWrapper, error: Boolean(formik.errors['displayName']), helperText: formik.errors['displayName'] }, void 0), (sports === null || sports === void 0 ? void 0 : sports.length) > 0 && (_jsxs(FormControl, __assign({ className: "w-full" }, { children: [_jsx(InputLabel, { children: "\u0412\u0438\u0434\u044B \u0441\u043F\u043E\u0440\u0442\u0430" }, void 0), _jsx(Select, __assign({ label: "\u0412\u0438\u0434\u044B \u0441\u043F\u043E\u0440\u0442\u0430", multiple: true, value: sportNames, onChange: handleSportsChange, input: _jsx(OutlinedInput, { className: "\n\t\t\t\t\t\t\tbg-bg-container-inner-lt \n\t\t\t\t\t\t\tdark:bg-bg-container-inner-dk  \n\t\t\t\t\t\t\tdark:text-white", label: "\u0412\u0438\u0434\u044B \u0441\u043F\u043E\u0440\u0442\u0430" }, void 0), renderValue: function (selected) { return (_jsx(Box, __assign({ sx: { display: 'flex', flexWrap: 'wrap', gap: 0.5 } }, { children: selected.map(function (value) { return (_jsx(Chip, { label: value }, value)); }) }), void 0)); }, MenuProps: MenuProps }, { children: sports.map(function (sport) { return (_jsx(MenuItem, __assign({ value: sport, style: getStyles(sport, sportNames, theme) }, { children: sport }), sport)); }) }), void 0)] }), void 0)), _jsx(DateTimePicker, { mask: "__/__/____ __:__", inputFormat: "dd/MM/yyyy HH:mm", label: "\u0414\u0430\u0442\u0430 \u043D\u0430\u0447\u0430\u043B\u0430", value: formik.values['activityStartDate'] && new Date(formik.values['activityStartDate']), minDate: new Date(tournamentStartDate), maxDate: new Date(tournamentEndDate), onChange: function (date) {
                                    formik.setFieldError('activityStartDate', undefined);
                                    formik.setFieldValue('activityStartDate', date);
                                }, renderInput: function (params) { return (_jsx(TextField, __assign({}, params, { error: Boolean(formik.errors['activityStartDate']), helperText: formik.errors['activityStartDate'] }), void 0)); } }, void 0), _jsx(DateTimePicker, { mask: "__/__/____ __:__", inputFormat: "dd/MM/yyyy HH:mm", label: "\u0414\u0430\u0442\u0430 \u043E\u043A\u043E\u043D\u0447\u0430\u043D\u0438\u044F", value: formik.values['activityEndDate'] && new Date(formik.values['activityEndDate']), minDate: new Date(tournamentStartDate), maxDate: new Date(tournamentEndDate), onChange: function (date) {
                                    formik.setFieldError('activityEndDate', undefined);
                                    formik.setFieldValue('activityEndDate', date);
                                }, renderInput: function (params) { return (_jsx(TextField, __assign({}, params, { error: Boolean(formik.errors['activityEndDate']), helperText: formik.errors['activityEndDate'] }), void 0)); } }, void 0), _jsxs(FormControl, __assign({ className: "w-full" }, { children: [_jsx(InputLabel, { children: "\u0422\u0438\u043F \u0437\u0430\u0447\u0451\u0442\u0430" }, void 0), _jsx(Select, __assign({ label: "\u0422\u0438\u043F \u0437\u0430\u0447\u0451\u0442\u0430", value: participantType, onChange: handleTournamentStandingsTypeChange, input: _jsx(OutlinedInput, { className: "\n\t\t\t\t\t\t\tbg-bg-container-inner-lt \n\t\t\t\t\t\t\tdark:bg-bg-container-inner-dk  \n\t\t\t\t\t\t\tdark:text-white", label: "\u0422\u0438\u043F \u0437\u0430\u0447\u0451\u0442\u0430" }, void 0), error: Boolean(formik.errors['participantType']) }, { children: participantTypes.map(function (participantType) { return (_jsx(MenuItem, __assign({ value: participantType }, { children: participantType }), participantType)); }) }), void 0)] }), void 0)] }), void 0)] }), void 0), _jsxs(DialogActions, __assign({ className: "flex justify-between p-0" }, { children: [_jsx(Button, __assign({ variant: "text", type: "reset", onClick: handleClose }, { children: t('Отмена') }), void 0), _jsx(Button, __assign({ type: "submit" }, { children: t('Создать') }), void 0)] }), void 0)] }), void 0));
};
export default CreateTournamentStandingsDialogDialog;
